import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-otp-step',
  templateUrl: './otp-step.component.html',
  styleUrls: ['./otp-step.component.scss'],
  standalone: true,
  imports: [CommonModule],
})
export class OtpStepComponent implements AfterViewInit, OnChanges {
  @Input() otp = '';
  @Input() otpError = '';
  @Input() otpSentTo = '';
  @Input() countdown = 0;
  @Input() isLoading = false;

  @Output() otpChange = new EventEmitter<string>();
  @Output() verified = new EventEmitter<void>();
  @Output() resent = new EventEmitter<void>();
  @Output() backed = new EventEmitter<void>();

  readonly cells = [0, 1, 2, 3, 4, 5];
  digits: string[] = ['', '', '', '', '', ''];
  shake = false;

  @ViewChildren('cell') cellInputs!: QueryList<ElementRef<HTMLInputElement>>;

  get isComplete(): boolean {
    return this.digits.every((d) => d !== '');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['otp'] && (this.otp || '') !== this.digits.join('')) {
      const clean = (this.otp || '').replace(/\D/g, '').slice(0, 6);
      this.digits = Array.from({ length: 6 }, (_, i) => clean[i] ?? '');
      this.syncInputs();
    }

    if (changes['otpError'] && this.otpError && !changes['otpError'].firstChange) {
      this.shake = true;
      setTimeout(() => (this.shake = false), 450);
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.focusCell(this.firstEmptyIndex()), 60);
  }

  onInput(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const clean = input.value.replace(/\D/g, '');

    if (!clean) {
      this.digits[index] = '';
      this.syncInputs();
      this.emitOtp();
      return;
    }

    let i = index;
    for (const ch of clean) {
      if (i > 5) break;
      this.digits[i] = ch;
      i++;
    }
    this.syncInputs();
    this.emitOtp();

    if (this.isComplete) {
      this.focusCell(5);
    } else {
      this.focusCell(this.firstEmptyIndex(Math.min(i, 5)));
    }
  }

  onKeyDown(index: number, event: KeyboardEvent): void {
    const key = event.key;

    if (key === 'Backspace') {
      event.preventDefault();
      if (this.digits[index]) {
        this.digits[index] = '';
      } else if (index > 0) {
        this.digits[index - 1] = '';
        this.focusCell(index - 1);
      }
      this.syncInputs();
      this.emitOtp();
      return;
    }

    if (key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      this.focusCell(index - 1, true);
      return;
    }

    if (key === 'ArrowRight' && index < 5) {
      event.preventDefault();
      this.focusCell(index + 1, true);
      return;
    }

    if (key === 'Enter') {
      if (this.isComplete && !this.isLoading) {
        this.verified.emit();
      }
      return;
    }

    if (key.length === 1 && !/\d/.test(key) && !event.ctrlKey && !event.metaKey) {
      event.preventDefault();
    }
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const clean = (event.clipboardData?.getData('text') || '')
      .replace(/\D/g, '')
      .slice(0, 6);
    if (!clean) {
      return;
    }
    this.digits = Array.from({ length: 6 }, (_, i) => clean[i] ?? '');
    this.syncInputs();
    this.emitOtp();
    if (this.isComplete) {
      this.focusCell(5);
    } else {
      this.focusCell(this.firstEmptyIndex());
    }
  }

  onFocus(event: FocusEvent): void {
    (event.target as HTMLInputElement).select();
  }

  private emitOtp(): void {
    this.otpChange.emit(this.digits.join(''));
  }

  private syncInputs(): void {
    this.cellInputs?.forEach((ref, i) => {
      ref.nativeElement.value = this.digits[i];
    });
  }

  private focusCell(index: number, select = false): void {
    const el = this.cellInputs?.get(index)?.nativeElement;
    if (!el) {
      return;
    }
    el.focus();
    if (select) {
      el.select();
    }
  }

  private firstEmptyIndex(from = 0): number {
    for (let i = 0; i < 6; i++) {
      if (this.digits[i] === '') {
        return i;
      }
    }
    return Math.min(from, 5);
  }
}